import { BackSvg, Body, breakpoints, Button, Icon, Margin, Spacer } from '@spritz-finance/ui'
import { Outlet } from 'react-router'
import styled from 'styled-components'
import gradientBg from '../assets/img/app-bg.png'
import gradientBgAlt from '../assets/img/app-bg-not-connected.png'
import { useWeb3Connection } from '../web3/Web3Provider'
import { ImpersonationWarning } from './ImpersonationWarning'
import { PageContent } from './Layout/PageContainer'
import { TopNav } from './TopNav/TopNav'
import { Row } from './Layout/Row'
import { useScreenSize } from '../context/useScreenSize'
import { isQuickswap } from '../theme/utils'
import { QuickswapFooter } from './theme/quickswap/QuickswapFooter'
import {
    TestPaymentInfoDialogProvider,
    TestPaymentLearnMoreWrapper,
    useTestPaymentInfoDialog,
} from '../modules/bills/useTestPaymentInfoDialog'
import { VerificationProvider } from '../modules/verification/VerificationContext'
import React, { useState } from 'react'
import { CloseIcon } from './icons/CloseIcon'
import { useCurrentUser } from '../context/useCurrentUser'

const PageWrapper = styled.div<{ $connected: boolean }>`
    height: 100vh;
    width: 100vw;
    display: flex;
    flex-direction: column;

    ${breakpoints.xsmall} {
        flex-direction: column;
        min-height: -webkit-fill-available;
        min-height: fill-available;
    }

    background: var(
        --page-background,
        url('${({ $connected }) => ($connected ? gradientBg : gradientBgAlt)}') top/cover no-repeat
    );
`

const PageContentInner = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 0 ${Margin.Large};

    ${breakpoints.xsmall} {
        padding: 0;
    }
`

const BackLinkWrapper = styled.div`
    max-width: 1300px;
    width: 1300px;
    display: flex;
    margin: 0 auto;
    padding: 0 24px;
    height: 0;
`
const LinkBack = styled(Button.Text)`
    position: absolute;
    text-decoration: none;
    z-index: 1;
    &:hover {
        cursor: pointer;
    }
`

const QuickswapBackLink = () => {
    const { isDesktop } = useScreenSize()
    const quickswap = isQuickswap()

    if (!isDesktop || !quickswap) return null
    return (
        <BackLinkWrapper>
            <LinkBack onClick={() => window.open('https://quickswap.exchange/#/swap')}>
                <Row>
                    <Icon icon={BackSvg} tint="#448AFF" />
                    <Spacer width={4} />
                    <Body.SmBold size={16} color="#448AFF">
                        Back to Quickswap
                    </Body.SmBold>
                </Row>
            </LinkBack>
        </BackLinkWrapper>
    )
}

const BannerWrapper = styled.div`
    background: linear-gradient(0deg, rgba(221, 242, 104, 0.85), rgba(221, 242, 104, 0.85)), #ffffff;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 24px;
    position: relative;
`

const CloseIconWrapper = styled.div`
    position: absolute;
    right: 12px;
`

const TestPayWelcomeBanner = () => {
    const { me } = useCurrentUser()

    const storageKey = me.id + 'testbanner'

    const { isMobile } = useScreenSize()
    const timeSinceCreation = new Date().getTime() - new Date(me?.createdAt ?? 0).getTime()
    const isWithin7DaysSinceCreation = timeSinceCreation / 1000 / 86400 < 7
    const didHideBanner = localStorage.getItem(storageKey)
    const initialShow = isWithin7DaysSinceCreation && !didHideBanner

    const [show, setShow] = useState(initialShow)
    const { openDialog } = useTestPaymentInfoDialog()

    const hideBanner = () => {
        localStorage.setItem(storageKey, 'go away')
        setShow(false)
    }

    if (!show) {
        return null
    }

    return (
        <BannerWrapper>
            <Body.SmBold size={isMobile ? 14 : 16} align="center">
                Welcome to Spritz! Get up to $10 towards your first payment on every bill. Connect
                your bills within 7 days to claim this offer. &nbsp;
                <TestPaymentLearnMoreWrapper onClick={openDialog}>
                    Learn more
                </TestPaymentLearnMoreWrapper>
            </Body.SmBold>
            <Spacer width={8} />
            <CloseIconWrapper onClick={hideBanner}>
                <CloseIcon />
            </CloseIconWrapper>
        </BannerWrapper>
    )
}

export const PageLayout = () => {
    const { connected } = useWeb3Connection()
    return (
        <PageWrapper $connected={connected}>
            <TestPaymentInfoDialogProvider>
                <VerificationProvider>
                    <ImpersonationWarning />
                    <TopNav />
                    <TestPayWelcomeBanner />
                    <PageContent>
                        <QuickswapBackLink />
                        <PageContentInner>
                            <Outlet />
                        </PageContentInner>
                        <QuickswapFooter />
                    </PageContent>
                </VerificationProvider>
            </TestPaymentInfoDialogProvider>
        </PageWrapper>
    )
}
